import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import WhiteLogo from "../../assets/images/white-logo.svg";
import Logo from "../../assets/images/logo.svg";
import ImgPhone from "../../assets/images/login/mobiles.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import Loader from "../../components/common/Loader";
import authService from "../../Services/authService";
import { checkPasswordRules } from "../../utils/passwordValidator";
import PasswordStrengthChecklist from "../../components/common/PasswordStrengthChecklist";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const email = searchParams.get("email") || "";

    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [generalError, setGeneralError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [countdown, setCountdown] = useState(3);

    // Verify token on mount
    useEffect(() => {
        let isMounted = true;

        if (!token) {
            setVerifying(false);
            setTokenValid(false);
            setTokenError("No reset token provided in URL.");
            return;
        }

        authService.verifyResetToken(token)
            .then(() => {
                if (isMounted) {
                    setTokenValid(true);
                    setVerifying(false);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    setTokenValid(false);
                    setTokenError(err.message || "Invalid or expired password reset link.");
                    setVerifying(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [token]);

    // Countdown and redirect on success
    useEffect(() => {
        let timer;
        if (success) {
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        navigate("/login", { replace: true });
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [success, navigate]);

    // Password validation rules
    const rules = checkPasswordRules(password, { email });
    const isAllRulesMet = rules.isValid;

    const validateForm = () => {
        const errors = {};

        if (!password) {
            errors.password = "New password is required";
        } else if (!isAllRulesMet) {
            errors.password = "Password does not meet the security criteria";
        }

        if (!confirmPassword) {
            errors.confirmPassword = "Confirm password is required";
        } else if (password !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGeneralError(null);

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            await authService.resetPassword(token, password, confirmPassword);
            setSuccess(true);
        } catch (err) {
            const msg = err.message || "Failed to reset password. Please try again.";
            if (msg.includes("Invalid or expired password reset token")) {
                setTokenValid(false);
                setTokenError(msg);
            } else if (
                msg.includes("Cannot use a recently used password") ||
                msg.includes("recently used password") ||
                msg.includes("cannot be the same") ||
                msg.includes("reuse")
            ) {
                setFieldErrors({ newPassword: msg });
            } else if (msg.includes("match")) {
                setFieldErrors({ confirmPassword: msg });
            } else {
                setGeneralError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {(verifying || submitting) && <Loader />}

            <div className="login-page">
                {/* Left Section with Carousel */}
                <div className="left-section">
                    <div className="custome-coursole">
                        <Swiper pagination={true} modules={[Pagination]} className="mySwiper">
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone Illustration" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Account Security</h5>
                                        <p>Create a strong and unique password to safeguard hospital workflows and confidential patient records.</p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div className="white-logo">
                        <img src={WhiteLogo} alt="Pran Health Logo" />
                    </div>
                </div>

                {/* Right Section */}
                <div className="right-section">
                    <div className="logo-section-login">
                        <img src={Logo} alt="Hospital Logo" />
                    </div>

                    <div className="auth-flow-container">
                        {verifying ? (
                            <div className="auth-card-notice">
                                <div className="notice-icon-wrapper warning">
                                    <div className="spinner-border text-warning" role="status" style={{ width: "2rem", height: "2rem" }}>
                                        <span className="visually-hidden">Verifying...</span>
                                    </div>
                                </div>
                                <h3>Verifying Link</h3>
                                <p>Checking the validity of your password reset link. Please wait a moment...</p>
                            </div>
                        ) : !tokenValid ? (
                            <div className="auth-card-notice">
                                <div className="notice-icon-wrapper error">
                                    <i className="material-icons">link_off</i>
                                </div>
                                <h3>Link Expired or Invalid</h3>
                                <p>
                                    {tokenError || "This password reset link is invalid or has expired (links are valid for 15 minutes)."}
                                </p>
                                <div className="d-flex flex-column gap-2">
                                    <button
                                        type="button"
                                        className="customeBtn successBtn w-100 py-2"
                                        onClick={() => navigate("/forgot-password")}
                                    >
                                        Request New Link
                                    </button>
                                    <div className="back-to-login">
                                        <Link to="/login">
                                            <i className="material-icons">arrow_back</i> Back to Login
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : success ? (
                            <div className="auth-card-notice">
                                <div className="notice-icon-wrapper success">
                                    <i className="material-icons">check_circle</i>
                                </div>
                                <h3>Password Reset Successful!</h3>
                                <p>
                                    Your password has been updated successfully. Redirecting you to login in <strong>{countdown}</strong> seconds...
                                </p>
                                <div className="d-flex flex-column gap-2">
                                    <button
                                        type="button"
                                        className="customeBtn successBtn w-100 py-2"
                                        onClick={() => navigate("/login", { replace: true })}
                                    >
                                        Go to Login Now
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="login-section" style={{ width: "100%" }}>
                                <div className="auth-flow-header">
                                    <h4>Create New Password</h4>
                                    <p>Choose a secure password for your account.</p>
                                </div>

                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="loginFeilds">
                                        {generalError && (
                                            <div className="error-message" role="alert">
                                                {generalError}
                                            </div>
                                        )}

                                        {/* New Password */}
                                        <div className="feilds">
                                            <label htmlFor="reset-new-password">New Password</label>
                                            <div className="password-input-group">
                                                <input
                                                    id="reset-new-password"
                                                    type={showPassword ? "text" : "password"}
                                                    name="newPassword"
                                                    autoComplete="new-password"
                                                    autoFocus
                                                    value={password}
                                                    onChange={(e) => {
                                                        setPassword(e.target.value);
                                                        if (fieldErrors.password || fieldErrors.newPassword) {
                                                            setFieldErrors((prev) => ({ ...prev, password: "", newPassword: "" }));
                                                        }
                                                    }}
                                                    placeholder="Enter new password"
                                                    className={fieldErrors.password || fieldErrors.newPassword ? "error" : ""}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="password-toggle-btn"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    <i className="material-icons">
                                                        {showPassword ? "visibility_off" : "visibility"}
                                                    </i>
                                                </button>
                                            </div>
                                            {(fieldErrors.password || fieldErrors.newPassword) && (
                                                <small>{fieldErrors.password || fieldErrors.newPassword}</small>
                                            )}
                                        </div>

                                        {/* Password requirements checklist */}
                                        <PasswordStrengthChecklist rules={rules} title="Password must contain:" />

                                        {/* Confirm Password */}
                                        <div className="feilds">
                                            <label htmlFor="reset-confirm-password">Confirm Password</label>
                                            <div className="password-input-group">
                                                <input
                                                    id="reset-confirm-password"
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirmPassword"
                                                    autoComplete="new-password"
                                                    value={confirmPassword}
                                                    onChange={(e) => {
                                                        setConfirmPassword(e.target.value);
                                                        if (fieldErrors.confirmPassword) {
                                                            setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
                                                        }
                                                    }}
                                                    placeholder="Repeat new password"
                                                    className={fieldErrors.confirmPassword ? "error" : ""}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="password-toggle-btn"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                >
                                                    <i className="material-icons">
                                                        {showConfirmPassword ? "visibility_off" : "visibility"}
                                                    </i>
                                                </button>
                                            </div>
                                            {fieldErrors.confirmPassword && (
                                                <small>{fieldErrors.confirmPassword}</small>
                                            )}
                                        </div>

                                        <div className="feilds mt-3">
                                            <button
                                                type="submit"
                                                className="customeBtn successBtn w-100 px-3 py-2"
                                                disabled={submitting}
                                            >
                                                {submitting ? "Updating Password..." : "Reset Password"}
                                            </button>
                                        </div>

                                        <div className="back-to-login">
                                            <Link to="/login">
                                                <i className="material-icons">arrow_back</i> Back to Login
                                            </Link>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;
